import { Container } from 'components/Custom'
import { SortHeader } from 'components/Custom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSidebarStore from 'store/useSidebar'
import { Rotas } from 'routes/rotas'
import { documentsSidebarComponents, documentsSidebarIds } from './sidebars/SidebarConfig'
import { Progress } from 'components/Custom/Progress'
import { OrderManagementEnum } from 'enums/selects'
import { Box } from '@mui/material'
import CardDocuments from './components/CardDocuments'
import { Typography } from 'components/Material/Typography'
import useTextStyles from 'styles/useTextStyles'
import { useIsMobile } from 'hooks/useIsResponsive'
import useDocumentsManagementStore from 'store/useDocumentsManagementStore'
import { useDocumentManagementHook } from 'hooks/useDocumentManagementHook'
import NotFoundAlert from 'components/Material/Icons/NotFound'
import { sortDocuments } from 'utils/sortFunctions'
import { useHandlerPrivileges } from 'hooks/useHandlerPrivileges'
import { PrivilegesEnum } from 'enums/privileges'
import { SortDocumentosType } from 'types/sortFunctions'
import { DocumentApprovalViewer } from './components/DocumentApprovalViewer'

const DocumentManagement = () => {
  const { activeSidebar, openSidebar } = useSidebarStore((state) => state)
  const isMobile = useIsMobile()
  const [sortOrder, setSortOrder] = useState<keyof SortDocumentosType>('proposal')
  const isOpen = Object.values(documentsSidebarIds).includes(activeSidebar)
  const {
    currentFilter,
    proposalsData,
    sortedProposalsData,
    setSortedProposalsData,
    resetFilter,
    defaultFilter,
    setCurrentFilter
  } = useDocumentsManagementStore((state) => state)
  const [isSelected, setIsSelected] = useState(0)
  const { isLoading, handleCallGetListDocuments } = useDocumentManagementHook()
  const hasReset = useRef(false)
  const handlerPrivileges = useHandlerPrivileges()

  useEffect(() => {
    handlerPrivileges(PrivilegesEnum.sn_documentacoes)

    if (JSON.stringify(defaultFilter) !== JSON.stringify(currentFilter)) {
      resetFilter()
      hasReset.current = true
    }
  }, [])

  useEffect(() => {
    if (!hasReset.current) {
      handleCallGetListDocuments()
    } else {
      hasReset.current = false
    }
  }, [currentFilter])

  useEffect(() => {
    if (
      activeSidebar === documentsSidebarIds.FILTRO ||
      (activeSidebar !== documentsSidebarIds.DETALHES &&
        activeSidebar !== documentsSidebarIds.UPLOAD_FILE &&
        activeSidebar !== documentsSidebarIds.DOWNLOAD_FILE)
    ) {
      setIsSelected(0)
    }
  }, [activeSidebar])

  const sortProposalsList = useCallback(() => {
    if (!proposalsData) return
    const sortedData = [...proposalsData].sort(sortDocuments[sortOrder])
    setSortedProposalsData(sortedData)
  }, [sortOrder, proposalsData])

  useEffect(() => {
    sortProposalsList()
  }, [sortOrder, proposalsData, sortProposalsList])

  useEffect(() => {
    if (!isMobile) {
      openSidebar(documentsSidebarIds.FILTRO)
    }
  }, [isMobile, isOpen, openSidebar])

  const memoizedSidebarComponents = useMemo(() => {
    return documentsSidebarComponents({
      defaultFilter,
      onSubmit: setCurrentFilter,
      currentFilter,
      handleCallGetListDocuments
    })
  }, [])

  const handleSelectChange = (value: string) => {
    setSortOrder(value as keyof SortDocumentosType)
  }
  return (
    <Container
      route={Rotas.MENU}
      leftText="Documentações"
      showButton={activeSidebar !== documentsSidebarIds.DOWNLOAD_FILE}
      activeSidebar={
        memoizedSidebarComponents[activeSidebar as keyof typeof memoizedSidebarComponents]
      }
      imagePage={
        activeSidebar === documentsSidebarIds.DOWNLOAD_FILE ? (
          <DocumentApprovalViewer sidbarToReturn={documentsSidebarIds.DETALHES} />
        ) : undefined
      }
    >
      <SortHeader
        title="Documentações"
        sortItems={OrderManagementEnum}
        onSelectChange={handleSelectChange}
        orderSelected={sortOrder}
        qnt_found={proposalsData.length}
        hasFilterButton={isMobile ? documentsSidebarIds.FILTRO : undefined}
        isLoading={isLoading}
      />
      {isLoading ? (
        <Box className="flex justify-center py-10">
          <Progress />
        </Box>
      ) : sortedProposalsData && sortedProposalsData.length > 0 ? (
        <Box className=" w-full flex flex-wrap gap-5 p-5 justify-center overflow-auto pb-20">
          {sortedProposalsData?.map((item) => (
            <CardDocuments
              key={item.id_documento}
              proposal={item}
              isSelected={isSelected === item.id_documento}
              onClick={() => setIsSelected(item.id_documento)}
            />
          ))}
        </Box>
      ) : (
        <Box className="flex justify-center p-5">
          <Typography sx={{ ...useTextStyles.titleLarge }}>
            <NotFoundAlert />
          </Typography>
        </Box>
      )}
    </Container>
  )
}

export default DocumentManagement
